import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ArrowDownRight, ArrowUpRight } from "react-bootstrap-icons";
import {
  useGetGeoBimDownloadsFullQuery,
  useGetLoginsPerMonthQuery,
  useGetUsersPerCountryQuery,
  useGetUsersPerMonthQuery,
} from "../../services/API/statisticsAPI";

// Percent change vs. previous period; null when there is no base to compare against
const percentDelta = (current, previous) => {
  if (!previous) return null;
  return Math.round(((current - previous) / previous) * 100);
};

const findMonthEntry = (data, year, month) =>
  data?.find((item) => item?._id?.year === year && item?._id?.month === month);

const KpiCard = ({ label, value, delta, deltaLabel, hint }) => (
  <div className="kpi-card">
    <div className="kpi-card__label">{label}</div>
    <div className="kpi-card__value">{value == null ? "–" : value.toLocaleString()}</div>
    {delta != null && (
      <div
        className={`kpi-card__delta ${delta >= 0 ? "kpi-card__delta--up" : "kpi-card__delta--down"}`}
      >
        {delta >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
        {`${delta > 0 ? "+" : ""}${delta} % ${deltaLabel}`}
      </div>
    )}
    {hint && <div className="kpi-card__hint">{hint}</div>}
  </div>
);

const KpiCards = () => {
  const { t } = useTranslation(["translation", "statistics"]);
  const todayMonth = new Date().getMonth();
  const todayYear = new Date().getFullYear();
  // Aggregation _ids use 1-based months ($month), JS Date is 0-based
  const monthNow = todayMonth + 1;

  // The API ignores query params on these endpoints — every caller passes
  // undefined so all components share one cache entry per endpoint
  const { data: countries } = useGetUsersPerCountryQuery(undefined, {
    pollingInterval: 60000,
  });
  const { data: usersPerMonth } = useGetUsersPerMonthQuery(undefined, {
    pollingInterval: 60000,
  });
  const { data: loginsPerMonth } = useGetLoginsPerMonthQuery(undefined, {
    pollingInterval: 60000,
  });
  const { data: geobimDownloads } = useGetGeoBimDownloadsFullQuery(undefined, {
    pollingInterval: 60000,
  });

  const totalUsers = countries?.reduce((sum, item) => sum + item.count, 0);

  const registrationsThisMonth = findMonthEntry(usersPerMonth, todayYear, monthNow)?.count ?? 0;
  const registrationsSameMonthLastYear =
    findMonthEntry(usersPerMonth, todayYear - 1, monthNow)?.count ?? 0;

  const loginsThisMonth = findMonthEntry(loginsPerMonth, todayYear, monthNow)?.count ?? 0;
  const loginsSameMonthLastYear =
    findMonthEntry(loginsPerMonth, todayYear - 1, monthNow)?.count ?? 0;

  const geobimTotal = geobimDownloads?.length;
  const geobimThisMonth = Array.isArray(geobimDownloads)
    ? geobimDownloads.filter((item) => {
        if (!item?.downloadAt) return false;
        const d = new Date(item.downloadAt);
        return d.getFullYear() === todayYear && d.getMonth() === todayMonth;
      }).length
    : 0;

  const vsPreviousYear = t("kpi.vs_previous_year", { ns: "statistics" });

  return (
    <Row className="g-4 kpi-row">
      <Col sm={6} xl={3}>
        <KpiCard label={t("kpi.total_users", { ns: "statistics" })} value={totalUsers} />
      </Col>
      <Col sm={6} xl={3}>
        <KpiCard
          label={t("kpi.new_registrations_month", { ns: "statistics" })}
          value={usersPerMonth ? registrationsThisMonth : null}
          delta={percentDelta(registrationsThisMonth, registrationsSameMonthLastYear)}
          deltaLabel={vsPreviousYear}
        />
      </Col>
      <Col sm={6} xl={3}>
        <KpiCard
          label={t("kpi.logins_month_netto", { ns: "statistics" })}
          value={loginsPerMonth ? loginsThisMonth : null}
          delta={percentDelta(loginsThisMonth, loginsSameMonthLastYear)}
          deltaLabel={vsPreviousYear}
        />
      </Col>
      <Col sm={6} xl={3}>
        <KpiCard
          label={t("kpi.geobim_total", { ns: "statistics" })}
          value={geobimTotal}
          hint={
            geobimDownloads
              ? `+${geobimThisMonth} ${t("kpi.this_month", { ns: "statistics" })}`
              : null
          }
        />
      </Col>
    </Row>
  );
};

export default KpiCards;
